import { FC, useState } from "react";
import {
  Paper,
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import ReceiptIcon from "@mui/icons-material/Receipt";
import DeleteIcon from "@mui/icons-material/Delete";
import { RxBasicInfoDto } from "../../../../../../../api-client";
import { formatDateUI } from "../../../../../../../helpers/dateFormat";
import { useRemoveRxFromTransaction } from "../../../../../../../stores/Transactions/transaction.mutations";
import { DeleteRxConfirmDialog } from "./DeleteRxConfirmDialog";
import { useLocationId } from "../../../../../../../stores/User/User.hooks";

interface RxListPanelProps {
  rxs: RxBasicInfoDto[];
  onRxClick: (rx: RxBasicInfoDto) => void;
  selectedRxId: string | null;
  transactionId: string;
  onClearSelection?: () => void;
}

export const RxListPanel: FC<RxListPanelProps> = ({
  rxs,
  onRxClick,
  selectedRxId,
  transactionId,
  onClearSelection,
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rxToDelete, setRxToDelete] = useState<RxBasicInfoDto | null>(null);

  const removeRxMutation = useRemoveRxFromTransaction();
  const locationId = useLocationId();

  const handleDeleteClick = (e: React.MouseEvent, rx: RxBasicInfoDto) => {
    e.stopPropagation();
    setRxToDelete(rx);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = (reason: string) => {
    if (!rxToDelete) return;

    const wasSelected = rxToDelete.id === selectedRxId;

    removeRxMutation.mutate(
      {
        transactionId,
        rxId: rxToDelete.id,
        reason,
        locationId,
      },
      {
        onSuccess: () => {
          setDeleteDialogOpen(false);
          setRxToDelete(null);
          if (wasSelected && onClearSelection) {
            onClearSelection();
          }
        },
      },
    );
  };

  const handleCloseDialog = () => {
    setDeleteDialogOpen(false);
    setRxToDelete(null);
  };
  return (
    <Paper
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        borderRadius: 2,
        bgcolor: "#D9DFE2",
        border: "1px solid #e0e0e0",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 1,
          bgcolor: "white",
          color: "black",
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <ReceiptIcon fontSize="small" sx={{ color: "#757575" }} />
        <Typography variant="body2" fontWeight="500">
          Rezepte
        </Typography>
        {rxs.length > 0 && (
          <Chip
            label={rxs.length}
            size="small"
            sx={{
              bgcolor: "#003059",
              color: "white",
              height: 20,
              fontSize: "0.7rem",
              fontWeight: 500,
            }}
          />
        )}
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          p: 1,
          bgcolor: "#D9DFE2",
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f5f5f5",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#bdbdbd",
            borderRadius: "6px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#9e9e9e",
          },
        }}
      >
        {rxs.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: "text.secondary",
            }}
          >
            <ReceiptIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
            <Typography variant="caption" align="center">
              Keine Rezepte
            </Typography>
          </Box>
        ) : (
          <List dense sx={{ p: 0 }}>
            {rxs.map((rx) => (
              <ListItemButton
                key={rx.id}
                onClick={() => onRxClick(rx)}
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  mb: 0.5,
                  bgcolor:
                    selectedRxId === rx.id ? "#f7d6dc !important" : "white",
                  borderColor: selectedRxId === rx.id ? "#9C1A35" : "#e0e0e0",
                  "&:hover": {
                    bgcolor:
                      selectedRxId === rx.id ? "#f0b3be !important" : "#f5f5f5",
                    borderColor: "#9C1A35",
                  },
                }}
              >
                <ListItemText
                  primary={
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight="500"
                        color="#003059"
                      >
                        {rx.rxNumber}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight="500"
                        color="text.secondary"
                      >
                        {formatDateUI(rx.date)}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                      >
                        {rx.patient.firstName} {rx.patient.lastName}
                      </Typography>
                    </Box>
                  }
                />
                <Tooltip title="Rezept entfernen">
                  <IconButton
                    size="small"
                    onClick={(e) => handleDeleteClick(e, rx)}
                    disabled={removeRxMutation.isPending}
                    sx={{
                      color: "#9C1A35",
                      ml: 1,
                      "&:hover": {
                        bgcolor: "rgba(211, 47, 47, 0.1)",
                      },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </ListItemButton>
            ))}
          </List>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      {rxToDelete && (
        <DeleteRxConfirmDialog
          open={deleteDialogOpen}
          onClose={handleCloseDialog}
          onConfirm={handleConfirmDelete}
          rxNumber={rxToDelete.rxNumber}
          patientName={`${rxToDelete.patient.firstName} ${rxToDelete.patient.lastName}`}
          isPending={removeRxMutation.isPending}
        />
      )}
    </Paper>
  );
};
